// Phase K.1c-fe (2026-08-06) — Consolidated billing mode toggle.
//
// Super-admin-only UI for the K.1c PATCH endpoint
// (PATCH /api/admin/customers/{id}/billing).
//
// Placement: top of the customer detail "Statements" tab. Renders a
// compact card showing the current billing config + an "Edit" button
// that opens a modal with the mode radio + numeric inputs + reason.
// Non-super-admins see the READ-ONLY summary but no edit affordance.
//
// Server invariants enforced server-side; we mirror validation here so
// invalid submits are caught before the network round-trip:
//   * consolidated_billing_day: 1..28
//   * consolidated_charge_grace_days: 0..14
//   * reason: min 5 chars
//   * consolidated mode requires day + grace present
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Settings2, AlertTriangle } from "lucide-react";

const MODE_LABELS = {
    per_invoice: "Per-invoice",
    consolidated: "Consolidated (monthly statement)",
};

export default function CustomerBillingModeCard({
    customer,
    isSuperAdmin,
    // Phase K.7.1 (2026-08-21) — widened edit gate to admin role too,
    // matching the K.7 top-nav Statements widening and the BE PATCH
    // /admin/customers/{id}/billing which now accepts admin+super_admin.
    isAdmin,
    onSaved,
}) {
    const [open, setOpen] = useState(false);
    const canEditBilling = !!(isSuperAdmin || isAdmin);

    // Refresh card content when the parent reloads the customer doc.
    const current = useMemo(() => ({
        billing_mode: customer?.billing_mode || null,
        consolidated_billing_day: customer?.consolidated_billing_day ?? null,
        consolidated_charge_grace_days: customer?.consolidated_charge_grace_days ?? null,
    }), [customer]);

    const configured = current.billing_mode != null;

    return (
        <Card
            className="border-slate-200"
            data-testid="billing-settings-card"
        >
            <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <h3 className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-1">
                            Billing settings
                        </h3>
                        {!configured && (
                            <p className="text-sm text-slate-500"
                               data-testid="billing-settings-not-configured">
                                Not configured — customer defaults to per-invoice billing.
                            </p>
                        )}
                        {configured && (
                            <div className="text-sm text-slate-800 space-y-0.5"
                                 data-testid="billing-settings-current">
                                <div>
                                    Mode: <span className="font-medium"
                                                data-testid="billing-mode-value">
                                        {MODE_LABELS[current.billing_mode] || current.billing_mode}
                                    </span>
                                </div>
                                {current.billing_mode === "consolidated" && (
                                    <div className="text-xs text-slate-600">
                                        Statement day{" "}
                                        <span className="font-medium"
                                              data-testid="billing-day-value">
                                            {current.consolidated_billing_day}
                                        </span>{" "}
                                        · Auto-charge{" "}
                                        <span className="font-medium"
                                              data-testid="billing-grace-value">
                                            {current.consolidated_charge_grace_days}
                                        </span>{" "}
                                        day(s) after send
                                    </div>
                                )}
                                {current.billing_mode === "per_invoice" &&
                                 current.consolidated_billing_day != null && (
                                    <div className="text-xs text-slate-500">
                                        Consolidated config preserved:
                                        day {current.consolidated_billing_day},
                                        grace {current.consolidated_charge_grace_days ?? "—"}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {canEditBilling && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOpen(true)}
                            data-testid="edit-billing-settings-btn"
                        >
                            <Settings2 className="h-4 w-4 mr-1" />
                            Edit billing settings
                        </Button>
                    )}
                </div>
            </CardContent>

            <EditBillingModal
                open={open}
                onOpenChange={setOpen}
                customer={customer}
                onSaved={(next) => {
                    setOpen(false);
                    if (onSaved) onSaved(next);
                }}
            />
        </Card>
    );
}


function EditBillingModal({ open, onOpenChange, customer, onSaved }) {
    // Phase K.1c-fe hotfix (2026-08-06) — hydrate ALL form fields
    // directly from the customer prop's 3 billing fields whenever the
    // modal opens. Previously the effect depended on the `current`
    // useMemo reference which could go stale if the parent didn't
    // re-render between customer refetches. Reading the primitive
    // field values in the dep array is defensive against that class
    // of bug.
    const cm = customer?.billing_mode || null;
    const cd = customer?.consolidated_billing_day ?? null;
    const cg = customer?.consolidated_charge_grace_days ?? null;

    const [mode, setMode] = useState(cm || "per_invoice");
    const [day, setDay] = useState(cd != null ? String(cd) : "1");
    const [grace, setGrace] = useState(cg != null ? String(cg) : "3");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Re-hydrate every time the modal (re-)opens so a cancel-then-
    // reopen surfaces current-truth values, never sticky draft state.
    // Depends on the primitive field values (not the wrapper object)
    // so a stale useMemo reference in the parent can't produce a
    // silently-wrong default.
    useEffect(() => {
        if (open) {
            setMode(cm || "per_invoice");
            setDay(cd != null ? String(cd) : "1");
            setGrace(cg != null ? String(cg) : "3");
            setReason("");
            setError(null);
        }
    }, [open, cm, cd, cg]);

    const isConsolidated = mode === "consolidated";
    const dayN = Number(day);
    const graceN = Number(grace);

    const clientValid = (() => {
        if (reason.trim().length < 5) return "Reason must be at least 5 characters.";
        if (isConsolidated) {
            if (!Number.isInteger(dayN) || dayN < 1 || dayN > 28) {
                return "Statement day must be an integer 1–28.";
            }
            if (!Number.isInteger(graceN) || graceN < 0 || graceN > 14) {
                return "Grace days must be an integer 0–14.";
            }
        }
        return null;
    })();

    const preview = isConsolidated
        ? `This customer will be enrolled in consolidated monthly billing. Each month on day ${dayN || "?"}, all outstanding invoices are bundled into a single statement, emailed, then auto-charged via ACH ${graceN || "?"} day(s) later.`
        : "This customer will be billed per invoice. Any in-flight statement will finish on its own — the change only affects the next cycle.";

    const handleSubmit = async () => {
        if (clientValid) {
            setError(clientValid);
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const payload = { reason };
            if (mode !== cm) {
                payload.billing_mode = mode;
            }
            if (isConsolidated) {
                if (dayN !== cd) {
                    payload.consolidated_billing_day = dayN;
                }
                if (graceN !== cg) {
                    payload.consolidated_charge_grace_days = graceN;
                }
                // Ensure the server-side invariant is met even when only
                // the mode is changing (day/grace may not be on the doc).
                if (payload.consolidated_billing_day == null && cd == null) {
                    payload.consolidated_billing_day = dayN;
                }
                if (payload.consolidated_charge_grace_days == null && cg == null) {
                    payload.consolidated_charge_grace_days = graceN;
                }
            }
            const { data } = await api.patch(
                `/admin/customers/${customer.id}/billing`,
                payload,
            );
            toast.success(
                data.changed
                    ? "Billing settings updated."
                    : "No changes to apply.",
            );
            if (onSaved) onSaved(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-lg"
                data-testid="modal-billing"
            >
                <DialogHeader>
                    <DialogTitle>Billing settings</DialogTitle>
                    <DialogDescription>
                        Switch this customer between per-invoice and
                        consolidated monthly billing. Audit-logged.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label>Billing mode</Label>
                        <div className="space-y-2">
                            <label className="flex items-start gap-3 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                                <input
                                    type="radio"
                                    name="billing_mode"
                                    value="per_invoice"
                                    checked={mode === "per_invoice"}
                                    onChange={() => setMode("per_invoice")}
                                    data-testid="modal-billing-mode-per-invoice"
                                    className="mt-1"
                                />
                                <div>
                                    <div className="text-sm font-medium">Per-invoice</div>
                                    <div className="text-xs text-slate-500">
                                        Default. Each invoice is emailed and charged individually.
                                    </div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 rounded border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50">
                                <input
                                    type="radio"
                                    name="billing_mode"
                                    value="consolidated"
                                    checked={mode === "consolidated"}
                                    onChange={() => setMode("consolidated")}
                                    data-testid="modal-billing-mode-consolidated"
                                    className="mt-1"
                                />
                                <div>
                                    <div className="text-sm font-medium">Consolidated (monthly statement)</div>
                                    <div className="text-xs text-slate-500">
                                        All outstanding invoices in a period are bundled into a single statement and auto-charged.
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="billing_day">
                                Statement day (1–28)
                            </Label>
                            <Input
                                id="billing_day"
                                type="number"
                                min={1}
                                max={28}
                                value={day}
                                onChange={(e) => setDay(e.target.value)}
                                disabled={!isConsolidated}
                                data-testid="modal-billing-day"
                            />
                        </div>
                        <div>
                            <Label htmlFor="billing_grace">
                                Grace days (0–14)
                            </Label>
                            <Input
                                id="billing_grace"
                                type="number"
                                min={0}
                                max={14}
                                value={grace}
                                onChange={(e) => setGrace(e.target.value)}
                                disabled={!isConsolidated}
                                data-testid="modal-billing-grace"
                            />
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="billing_reason">
                            Reason <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                            id="billing_reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. K.2 pilot activation — enabling consolidated billing"
                            data-testid="modal-billing-reason"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                            Written verbatim into the customer_billing_mode_changed audit event.
                        </p>
                    </div>

                    <div
                        className="rounded border border-blue-200 bg-blue-50 text-blue-900 text-xs px-3 py-2"
                        data-testid="modal-billing-preview"
                    >
                        {preview}
                    </div>

                    {error && (
                        <div
                            className="flex items-start gap-2 rounded border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2"
                            data-testid="modal-billing-error"
                        >
                            <AlertTriangle className="h-4 w-4 mt-0.5 flex-none" />
                            <div>{error}</div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="modal-billing-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !!clientValid}
                        data-testid="modal-billing-submit"
                    >
                        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save billing settings
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
